import { Vector3, Quaternion } from 'three';

import { CameraState } from '@cognite/reveal';

export type CameraStateJSON = {
  position: Vector3JSON;
  target: Vector3JSON;
  rotation?: QuaternionJSON;
};

type Vector3JSON = { x: number; y: number; z: number };
type QuaternionJSON = { x: number; y: number; z: number; w: number };

/**
 * Extracts the serializable properties from a CameraState object.
 */
export const cameraStateToJSON = (
  cameraState: Required<CameraState> | undefined
): CameraStateJSON | undefined => {
  if (cameraState === undefined) {
    return undefined;
  }

  return {
    position: {
      x: cameraState.position.x,
      y: cameraState.position.y,
      z: cameraState.position.z,
    },
    target: {
      x: cameraState.target.x,
      y: cameraState.target.y,
      z: cameraState.target.z,
    },
    rotation: {
      x: cameraState.rotation.x,
      y: cameraState.rotation.y,
      z: cameraState.rotation.z,
      w: cameraState.rotation.w,
    },
  };
};

/**
 * Converts a CameraStateJSON object to a CameraState object.
 */
export const cameraStateFromJSON = (
  cameraState: CameraStateJSON
): {
  position: Vector3;
  target: Vector3;
  rotation?: Quaternion;
} => {
  return {
    position: new Vector3(
      cameraState.position.x,
      cameraState.position.y,
      cameraState.position.z
    ),
    target: new Vector3(
      cameraState.target.x,
      cameraState.target.y,
      cameraState.target.z
    ),
    rotation:
      cameraState.rotation !== undefined
        ? new Quaternion(
            cameraState.rotation.x,
            cameraState.rotation.y,
            cameraState.rotation.z,
            cameraState.rotation.w
          )
        : undefined,
  };
};

export function isCameraStateJSON(
  cameraState: unknown
): cameraState is CameraStateJSON {
  if (typeof cameraState !== 'object' || cameraState === null) {
    return false;
  }

  const hasValidPosition =
    'position' in cameraState && isVector3JSON(cameraState.position);

  if (!hasValidPosition) {
    return false;
  }

  const hasValidTarget =
    'target' in cameraState && isVector3JSON(cameraState.target);

  if (!hasValidTarget) {
    return false;
  }

  const hasRotation = 'rotation' in cameraState;
  const hasValidRotation =
    hasRotation && isQuaternionJSON(cameraState.rotation);

  if (hasRotation && !hasValidRotation) {
    return false;
  }

  return true;
}

const isVector3JSON = (vector: unknown): vector is Vector3JSON =>
  typeof vector === 'object' &&
  vector !== null &&
  typeof (vector as Vector3).x === 'number' &&
  typeof (vector as Vector3).y === 'number' &&
  typeof (vector as Vector3).z === 'number';

const isQuaternionJSON = (quaternion: unknown): quaternion is QuaternionJSON =>
  typeof quaternion === 'object' &&
  quaternion !== null &&
  typeof (quaternion as Quaternion).x === 'number' &&
  typeof (quaternion as Quaternion).y === 'number' &&
  typeof (quaternion as Quaternion).z === 'number' &&
  typeof (quaternion as Quaternion).w === 'number';
